import { useEffect, useState } from "react";
import { Loader2, Download, FileText } from "lucide-react";
import { getPreviewKind } from "@/lib/fileUtils";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useDocumentFile } from "@/hooks/useDocumentFile";
import { useDocumentViewer } from "@/contexts/DocumentViewerContext";
import PdfViewer from "@/components/PdfViewer/PdfViewer";

function parsePageNumber(page: string): number {
    const match = page.match(/#page=(\d+)/);
    return match ? parseInt(match[1], 10) : 1;
}

function parsePageFragment(page: string): string {
    const match = page.match(/page=(\d+)/);
    return match ? `&page=${match[1]}` : '';
}

export const DocumentViewerSheet = () => {
    const { viewingDoc: doc, viewingPage: page, sourceText, side, showTabs, hideOverlay, closeDocument } = useDocumentViewer();
    const { blobUrl, isLoading, error, errorStatus, fetchFile, downloadOriginal, clearFile } = useDocumentFile();
    // Set when the backend couldn't render an Office document to PDF, so the sheet offers the
    // original file for download instead of a preview.
    const [conversionFailed, setConversionFailed] = useState(false);
    const [isDownloading, setIsDownloading] = useState(false);

    const previewKind = getPreviewKind(doc?.filename ?? '');
    // Office formats have no in-browser renderer, so the API converts them to PDF and the
    // existing PdfViewer draws the result.
    const needsConversion = previewKind === 'convert';

    // Typed as PDF so the plain <iframe> preview renders it rather than downloading it.
    const previewOptions = needsConversion
        ? { format: 'pdf' as const, mimeType: 'application/pdf' }
        : previewKind === 'pdf' ? { mimeType: 'application/pdf' } : {};

    useEffect(() => {
        setConversionFailed(false);
        if (doc) fetchFile(doc.id, previewOptions);
    }, [doc?.id]);

    // A 422 means conversion failed, not that the file is missing — refetch the original so the
    // fallback pane can still offer a working download.
    useEffect(() => {
        if (errorStatus === 422 && doc && !conversionFailed) {
            setConversionFailed(true);
            fetchFile(doc.id);
        }
    }, [errorStatus, doc?.id, conversionFailed]);

    const handleOpenChange = (open: boolean) => {
        if (!open) { clearFile(); closeDocument(); }
    };

    // True when the pane can be drawn by PdfViewer — either a real PDF or a successful conversion.
    const renderAsPdf = !conversionFailed && (previewKind === 'pdf' || needsConversion);

    // The loaded blob is the original file unless it's a PDF rendering of an Office document;
    // in that case fetch the original so the download matches its filename.
    const handleDownload = async () => {
        if (!doc) return;
        const filename = doc.filename || "document";
        if (!(needsConversion && !conversionFailed) && blobUrl) {
            const link = document.createElement("a");
            link.href = blobUrl;
            link.download = filename;
            link.click();
            return;
        }
        setIsDownloading(true);
        try {
            await downloadOriginal(doc.id, filename);
        } catch (err) {
            console.error("Document download failed", err);
        } finally {
            setIsDownloading(false);
        }
    };

    const renderLoadingOrError = () => {
        // A 422 is handled by refetching the original for download, so don't flash an error.
        if (isLoading || errorStatus === 422) {
            return (
                <div className="flex flex-col items-center justify-center h-full gap-2 text-muted-foreground">
                    <Loader2 className="w-5 h-5 animate-spin" />
                    {needsConversion && !conversionFailed ? (
                        <>
                            <span>Preparing preview…</span>
                            <span className="text-xs">This can take a few seconds the first time.</span>
                        </>
                    ) : (
                        <span>Loading document…</span>
                    )}
                </div>
            );
        }
        if (error) {
            return (
                <div className="flex flex-col items-center justify-center h-full gap-3 text-sm text-muted-foreground px-8 text-center">
                    <FileText className="w-12 h-12 text-muted-foreground/30" />
                    <p className="font-medium">Could not load document preview</p>
                    <p className="text-xs text-destructive">{error}</p>
                    {doc && (
                        <button type="button" onClick={() => fetchFile(doc.id, previewOptions)} className="text-sm text-brand-teal hover:underline mt-1">
                            Try again
                        </button>
                    )}
                </div>
            );
        }
        return null;
    };

    return (
        <Sheet open={!!doc} onOpenChange={handleOpenChange} modal={!hideOverlay}>
            <SheetContent side={side} hideOverlay={hideOverlay} onInteractOutside={hideOverlay ? (e) => e.preventDefault() : undefined} className="w-full md:w-[60svw] sm:max-w-none flex flex-col p-0 [&>button]:text-white">
                <Tabs defaultValue="references" className="flex flex-col h-full overflow-hidden">
                    <SheetHeader className="px-3 py-6 bg-primary text-white shrink-0">
                        <div className="flex items-start justify-between gap-4 pr-8">
                            <div className="min-w-0 flex-1">
                                <SheetTitle className="text-white leading-snug">
                                    {doc?.title || doc?.filename}
                                </SheetTitle>
                                <SheetDescription asChild>
                                    <div className="text-xs truncate">
                                        {!renderAsPdf && doc?.filename && <span className="block text-white/70">{doc.filename}</span>}
                                        <div className="flex flex-wrap items-center gap-2 text-white mt-1">
                                            {doc?.author && <span className="text-xs">{doc.author}</span>}
                                            {doc?.year && <span className="text-xs px-1.5 py-0.5 rounded bg-muted text-black">{doc.year}</span>}
                                            {doc?.sourceType && <span className="text-xs px-1.5 py-0.5 rounded bg-brand-teal text-white">{doc.sourceType}</span>}
                                        </div>
                                    </div>
                                </SheetDescription>
                            </div>
                        </div>
                        <div className="flex items-center gap-2 shrink-0">
                            {showTabs && renderAsPdf && (
                                <TabsList className="w-fit">
                                    <TabsTrigger value="references">References</TabsTrigger>
                                    <TabsTrigger value="document">Document</TabsTrigger>
                                </TabsList>
                            )}
                            {blobUrl && (
                                <button
                                    type="button"
                                    onClick={handleDownload}
                                    disabled={isDownloading}
                                    className="flex items-center gap-1.5 text-sm px-3 py-1.5 rounded-md border hover:bg-muted hover:text-black transition-colors font-medium ml-auto disabled:opacity-60"
                                >
                                    {isDownloading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
                                    Download
                                </button>
                            )}
                        </div>
                    </SheetHeader>

                    <div className="flex-1 overflow-hidden flex flex-col">
                        {isLoading || error || errorStatus === 422 ? renderLoadingOrError() : (
                            <>
                                {showTabs && renderAsPdf ? (
                                    <>
                                        <TabsContent value="references" className="flex-1 overflow-hidden mt-0 data-[state=active]:flex data-[state=active]:flex-col">
                                            {blobUrl && (
                                                <PdfViewer
                                                    key={blobUrl}
                                                    pdfUrl={blobUrl}
                                                    initialPage={parsePageNumber(page)}
                                                    citation={{ sourceText }}
                                                    fileName={doc?.filename ?? 'document.pdf'}
                                                />
                                            )}
                                        </TabsContent>
                                        <TabsContent value="document" className="flex-1 overflow-hidden mt-0 data-[state=active]:flex data-[state=active]:flex-col">
                                            {blobUrl && (
                                                <iframe
                                                    src={`${blobUrl}#toolbar=1&navpanes=0${parsePageFragment(page)}`}
                                                    className="w-full flex-1 border-0"
                                                    title={doc?.title ?? "Document Preview"}
                                                />
                                            )}
                                        </TabsContent>
                                    </>
                                ) : renderAsPdf ? (
                                    blobUrl && (
                                        <iframe
                                            src={`${blobUrl}#toolbar=1&navpanes=0${parsePageFragment(page)}`}
                                            className="w-full flex-1 border-0"
                                            title={doc?.title ?? "Document Preview"}
                                        />
                                    )
                                ) : (
                                    // An unsupported format, or an Office document the server
                                    // couldn't convert. Say so rather than showing a blank pane.
                                    <div className="flex flex-col items-center justify-center h-full gap-3 text-sm text-muted-foreground px-8 text-center">
                                        <FileText className="w-12 h-12 text-muted-foreground/30" />
                                        <p className="font-medium">
                                            {conversionFailed
                                                ? "This document couldn't be prepared for preview"
                                                : "Preview isn't available for this file type"}
                                        </p>
                                        {doc?.filename && <p className="text-xs break-all">{doc.filename}</p>}
                                        {blobUrl && (
                                            <button
                                                type="button"
                                                onClick={handleDownload}
                                                disabled={isDownloading}
                                                className="flex items-center gap-1.5 text-sm px-3 py-1.5 rounded-md border hover:bg-muted transition-colors font-medium mt-1 disabled:opacity-60"
                                            >
                                                {isDownloading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
                                                Download to view
                                            </button>
                                        )}
                                    </div>
                                )}
                            </>
                        )}
                    </div>
                </Tabs>
            </SheetContent>
        </Sheet>
    );
};
